export type AgentShotRef = {
  id: number
  label: string
}

export type HierarchicalShotParsed = {
  nums: number[]
  letters: string
}

const MAX_NUMERIC_RANKS = 2
const FIRST_LETTER = "A"

export function formatShotLabel(sceneNumber: number | string, shotNumber: number | string): string {
  return `${sceneNumber}.${shotNumber}`
}

export function parseHierarchicalShotNumber(value: string | number | null | undefined): HierarchicalShotParsed {
  const raw = String(value ?? "").trim()
  const match = raw.match(/^(\d+(?:\.\d+)*)([A-Z]*)$/i)
  if (!match) return { nums: [0], letters: "" }
  return {
    nums: match[1].split(".").map((part) => Number.parseInt(part, 10)),
    letters: (match[2] ?? "").toUpperCase(),
  }
}

export function formatHierarchicalShotNumber(parsed: HierarchicalShotParsed): string {
  return `${parsed.nums.join(".")}${parsed.letters}`
}

function cmpHierarchical(left: HierarchicalShotParsed, right: HierarchicalShotParsed): number {
  const minLen = Math.min(left.nums.length, right.nums.length)
  for (let i = 0; i < minLen; i++) {
    if (left.nums[i] !== right.nums[i]) return left.nums[i] - right.nums[i]
  }
  if (left.nums.length === right.nums.length) {
    if (left.letters === right.letters) return 0
    return left.letters < right.letters ? -1 : 1
  }
  return left.nums.length - right.nums.length
}

function incrementLastNumeric(parsed: HierarchicalShotParsed): HierarchicalShotParsed {
  const nums = parsed.nums.slice()
  nums[nums.length - 1] += 1
  return { nums, letters: "" }
}

function incrementLetters(str: string): string {
  if (!str) return FIRST_LETTER
  const last = str[str.length - 1]
  if (last < "Z") {
    return str.slice(0, -1) + String.fromCharCode(last.charCodeAt(0) + 1)
  }
  return `${str}A`
}

function incrementLettersParsed(parsed: HierarchicalShotParsed): HierarchicalShotParsed {
  return { nums: parsed.nums.slice(), letters: incrementLetters(parsed.letters) }
}

function nextFree(parsed: HierarchicalShotParsed, taken: Set<string>): string {
  let cand = parsed
  for (;;) {
    const s = formatHierarchicalShotNumber(cand)
    if (!taken.has(s)) return s
    cand = cand.letters ? incrementLettersParsed(cand) : incrementLastNumeric(cand)
  }
}

function usable(cand: HierarchicalShotParsed, right: HierarchicalShotParsed, taken: Set<string>): boolean {
  return cmpHierarchical(cand, right) < 0 && !taken.has(formatHierarchicalShotNumber(cand))
}

/** Dewey-between preview matching It2tv::Shots::HierarchicalNumber.next */
export function nextHierarchicalShotNumber(
  left: string | number | null | undefined,
  right: string | number | null | undefined,
  taken: Iterable<string | number> = [],
): string {
  const takenSet = new Set(Array.from(taken, (v) => String(v)))
  const leftStr = left == null || String(left).trim() === "" ? "0" : String(left)
  const rightStr = right == null || String(right).trim() === "" ? "" : String(right)
  const leftParsed = parseHierarchicalShotNumber(leftStr)

  if (!rightStr) {
    const cand = leftParsed.letters ? incrementLettersParsed(leftParsed) : incrementLastNumeric(leftParsed)
    return nextFree(cand, takenSet)
  }

  const rightParsed = parseHierarchicalShotNumber(rightStr)

  let cand = incrementLastNumeric(leftParsed)
  while (takenSet.has(formatHierarchicalShotNumber(cand)) && cmpHierarchical(cand, rightParsed) < 0) {
    cand = incrementLastNumeric(cand)
  }
  if (usable(cand, rightParsed, takenSet)) return formatHierarchicalShotNumber(cand)

  if (leftParsed.nums.length < MAX_NUMERIC_RANKS) {
    for (let n = 1; n <= 10000; n++) {
      cand = { nums: [...leftParsed.nums, n], letters: "" }
      if (usable(cand, rightParsed, takenSet)) return formatHierarchicalShotNumber(cand)
      if (cmpHierarchical(cand, rightParsed) >= 0) break
    }
  }

  cand = incrementLettersParsed(leftParsed)
  while (takenSet.has(formatHierarchicalShotNumber(cand)) && cmpHierarchical(cand, rightParsed) < 0) {
    cand = incrementLettersParsed(cand)
  }
  if (usable(cand, rightParsed, takenSet)) return formatHierarchicalShotNumber(cand)

  throw new Error("could not assign shot number")
}

export function buildShotRefMap(refs: AgentShotRef[]): {
  byId: Map<number, string>
  byLabel: Map<string, number>
} {
  const byId = new Map<number, string>()
  const byLabel = new Map<string, number>()
  for (const ref of refs) {
    byId.set(ref.id, ref.label)
    // First occurrence wins for duplicate labels across episodes
    if (!byLabel.has(ref.label)) byLabel.set(ref.label, ref.id)
  }
  return { byId, byLabel }
}
